import React from "react";
import { Label } from "../Label";
import { LocationPicker } from "./LocationPicker";
import { PopupCampaignPlatform } from "../../api/generated/models/PopupCampaignPlatform";
import {
  TargetingErrors,
  TargetingFormState,
  TARGETING_AGE_MAX,
  TARGETING_BALANCE_MAX_KR,
} from "../../helpers/popupCampaignTargeting";

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6d4c91] focus:border-transparent";
const errorClass = "text-xs text-red-600 mt-1";

const PLATFORM_LABELS: Array<[PopupCampaignPlatform, string]> = [
  [PopupCampaignPlatform.IOS, "iOS app"],
  [PopupCampaignPlatform.ANDROID, "Android app"],
  [PopupCampaignPlatform.WEB_MOBILE, "Mobile web"],
  [PopupCampaignPlatform.WEB_DESKTOP, "Desktop web"],
];

interface Props {
  form: TargetingFormState;
  onChange: (patch: Partial<TargetingFormState>) => void;
  errors: TargetingErrors;
  resolvedCount: number | null;
  resolving: boolean;
  resolveFailed?: boolean;
}

const RangeRow: React.FC<{
  label: string;
  minValue: string;
  maxValue: string;
  onMin: (value: string) => void;
  onMax: (value: string) => void;
  minError?: string;
  maxError?: string;
  suffix?: string;
}> = ({ label, minValue, maxValue, onMin, onMax, minError, maxError, suffix }) => (
  <div>
    <Label>{label}</Label>
    <div className="flex items-center gap-2 mt-1">
      <input type="number" value={minValue} onChange={(e) => onMin(e.target.value)} placeholder="No minimum" className={inputClass} />
      <span className="text-gray-400">–</span>
      <input type="number" value={maxValue} onChange={(e) => onMax(e.target.value)} placeholder="No maximum" className={inputClass} />
      {suffix && <span className="text-sm text-gray-500 whitespace-nowrap">{suffix}</span>}
    </div>
    {(minError || maxError) && <p className={errorClass}>{minError || maxError}</p>}
  </div>
);

export const AudienceCard: React.FC<Props> = ({
  form, onChange, errors, resolvedCount, resolving, resolveFailed,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6 space-y-5">
    <div>
      <h3 className="font-medium">Audience</h3>
      <p className="text-xs text-gray-500 mt-1">
        Leave everything blank to reach all users. Selections combine — a user must match every
        dimension you set.
      </p>
    </div>

    <div>
      <Label>Gender</Label>
      <select
        value={form.gender}
        onChange={(e) => onChange({ gender: e.target.value as TargetingFormState["gender"] })}
        className={`${inputClass} mt-1`}
      >
        <option value="">Any</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
      </select>
      <p className="text-xs text-gray-500 mt-1">Derived from the CPR number, so it is known for every user.</p>
    </div>

    <RangeRow
      label="Age"
      minValue={form.ageMin}
      maxValue={form.ageMax}
      onMin={(ageMin) => onChange({ ageMin })}
      onMax={(ageMax) => onChange({ ageMax })}
      minError={errors.ageMin}
      maxError={errors.ageMax}
      suffix={`years (0–${TARGETING_AGE_MAX})`}
    />

    <div>
      <Label>Location</Label>
      <div className="mt-2">
        <LocationPicker
          form={form}
          onChange={onChange}
          resolvedCount={resolvedCount}
          resolving={resolving}
          resolveFailed={resolveFailed}
          error={errors.radiusKm || errors.radiusLat || errors.radiusLon}
        />
      </div>
      <p className="text-xs text-gray-500 mt-2">
        About a quarter of users have no postal code on file and can never match a location rule.
      </p>
    </div>

    <div>
      <Label>Platforms</Label>
      <div className="flex flex-wrap gap-4 mt-1">
        {PLATFORM_LABELS.map(([platform, label]) => (
          <label key={platform} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.platforms.includes(platform)}
              onChange={(e) =>
                onChange({
                  platforms: e.target.checked
                    ? [...form.platforms, platform]
                    : form.platforms.filter((p) => p !== platform),
                })
              }
            />
            {label}
          </label>
        ))}
      </div>
      <p className="text-xs text-gray-500 mt-1">
        None selected means every platform. iOS and Android are counted in the estimate, from who
        has that app installed; the two web platforms are not, because nothing recorded says who
        might open the web app — selecting one leaves the estimate covering every eligible user.
        Platform targeting only takes effect once the app release that sends it is live.
      </p>
    </div>

    <RangeRow
      label="Oister subscription tenure"
      minValue={form.oisterTenureMin}
      maxValue={form.oisterTenureMax}
      onMin={(oisterTenureMin) => onChange({ oisterTenureMin })}
      onMax={(oisterTenureMax) => onChange({ oisterTenureMax })}
      minError={errors.oisterTenureMin}
      maxError={errors.oisterTenureMax}
      suffix="months"
    />

    <RangeRow
      label="Oister Bonus tenure"
      minValue={form.appTenureMin}
      maxValue={form.appTenureMax}
      onMin={(appTenureMin) => onChange({ appTenureMin })}
      onMax={(appTenureMax) => onChange({ appTenureMax })}
      minError={errors.appTenureMin}
      maxError={errors.appTenureMax}
      suffix="months"
    />

    <div>
      <RangeRow
        label="Point balance"
        minValue={form.balanceMin}
        maxValue={form.balanceMax}
        onMin={(balanceMin) => onChange({ balanceMin })}
        onMax={(balanceMax) => onChange({ balanceMax })}
        minError={errors.balanceMin}
        maxError={errors.balanceMax}
        suffix={`kr (0–${TARGETING_BALANCE_MAX_KR.toLocaleString("en-US")})`}
      />
      <label className="flex items-center gap-2 text-sm mt-2">
        <input
          type="checkbox"
          checked={form.balanceHasEverEarned}
          onChange={(e) => onChange({ balanceHasEverEarned: e.target.checked })}
        />
        Only users who have earned points at some point
      </label>
      <p className="text-xs text-gray-500 mt-1">
        Combine that with a 0–0 balance to reach users who earned points and have spent them all.
      </p>
    </div>
  </div>
);
